// Поездка: паровоз трогается со станции, идёт полным ходом и снова встаёт у неё же.
//
// Ею открывается и закрывается бонус, см. ADR-51. Сама машина при этом стоит на месте:
// едет мир вокруг неё. Фон уводит гору со станцией вдаль и гонит землю мимо, см. ui/background.ts,
// а из-под котла бьёт густой пар, см. ui/smoke.ts.
//
// Голос у поездки свой и идёт он тремя дорожками: паровоз трогается, стучит колёсами и тормозит.
// Играет их дым, как играет и гудок: пар и голос машины врозь не ходят, см. ADR-49.
//
// Позванный поезд встаёт не сразу: полным ходом он проходит своё, а уже потом тормозит. Иначе
// разгон переходил бы прямо в остановку, и хода игрок не видел бы вовсе.
//
// Взгляд игры поездке не мешает: он подходит к мини-слоту своим ходом и в те же секунды,
// см. ui/camera.ts.

import type { Background } from './background';
import type { Smoke } from './smoke';

/**
 * Сколько поезд идёт полным ходом, прежде чем начать тормозить.
 *
 * Полный ход — единственное место поездки, где виден сам ход: земля идёт мимо машины ровно
 * и быстро, а гора за горой набегают слева. Бонус к этой поре открыл мини-слот и крутит его,
 * и три секунды хода игрок смотрит уже на него.
 *
 * Позови станцию раньше — поезд разогнался бы и тут же встал.
 */
const RUN_SECONDS: number = 3;

/** Что поезд делает сейчас. */
type Phase = 'still' | 'riding' | 'called' | 'braking';

/** Поездка паровоза от станции до станции. */
export class Ride {
  readonly #background: Background;
  readonly #smoke: Smoke;

  #phase: Phase = 'still';

  /**
   * Номер поездки. По нему прибытие узнаёт, своё оно или от прошлой поездки.
   *
   * Игрок вправе оборвать бонус, и следующий застаёт поезд ещё на подходе к станции. Тот поезд
   * отправляется заново, а его прибытие доигрывает и приходит уже ни к чему.
   */
  #number = 0;

  /** Сколько идёт поездка. По ней встаёт стук колёс и по ней же поезд не тормозит раньше срока. */
  #riding = 0;

  /** Кто ждёт, пока поезд встанет. Будит их само прибытие. */
  #stopped: (() => void)[] = [];

  constructor(background: Background, smoke: Smoke) {
    this.#background = background;
    this.#smoke = smoke;
  }

  /**
   * Отправляет поезд.
   *
   * Ждать разгон некому: он идёт своим чередом и переходит в ровный ход, а игра тем временем
   * ведёт бонус. Кончает поездку прибытие, и только оно.
   *
   * Идущий поезд второй раз не отправляют — он и так идёт. Тормозящий отправляется заново,
   * и ждавшие того прибытия просыпаются: поезд к станции уже не придёт.
   */
  depart(): void {
    if (this.#phase === 'riding' || this.#phase === 'called')
      return;

    this.#number += 1;
    this.#phase = 'riding';
    this.#riding = 0;

    this.#background.depart();
    this.#smoke.hold('trainStarts');

    this.#wake();
  }

  /**
   * Зовёт поезд к станции и ждёт, пока он встанет.
   *
   * Тормозить он начинает не по зову, а пройдя полным ходом своё, см. RUN_SECONDS. Звать
   * его можно сколько угодно раз: все зовущие ждут одну и ту же остановку. Стоящий поезд
   * отвечает сразу — приводить некого.
   */
  arrive(): Promise<void> {
    if (this.#phase === 'still')
      return Promise.resolve();

    if (this.#phase === 'riding')
      this.#phase = 'called';

    return new Promise<void>((resolve) => {
      this.#stopped.push(resolve);
    });
  }

  /**
   * Ведёт поездку: меняет голос разгона на стук колёс и отпускает поезд к станции.
   *
   * Стук встаёт там, где кончился разгон. Дорожка разгона длиннее самого разгона на экране,
   * поэтому её остаток уводится, а не дослушивается: на полном ходу паровоз уже не трогается.
   */
  update(seconds: number): void {
    if (this.#phase === 'still' || this.#phase === 'braking')
      return;

    const started: number = this.#background.departSeconds;
    const passed: number = this.#riding;

    this.#riding += seconds;

    if (passed < started && this.#riding >= started)
      this.#smoke.hold('trainRuns');

    if (this.#phase !== 'called' || this.#riding < started + RUN_SECONDS)
      return;

    this.#phase = 'braking';
    this.#brake();
  }

  /** Тормозит поезд у станции и будит тех, кто этого ждал. */
  #brake(): void {
    const ride: number = this.#number;

    this.#smoke.start('trainStops');

    void this.#background.arrive().then((): void => {
      // Поезд мог тронуться снова, не доехав до станции: тогда это прибытие уже чужое.
      if (this.#number !== ride)
        return;

      this.#phase = 'still';

      // Пар уходит вместе с голосом тормозящей машины: поезд встал, и бить ему больше нечем.
      this.#smoke.stop();
      this.#wake();
    });
  }

  /** Будит всех, кто ждал остановки. Второй раз будить некого. */
  #wake(): void {
    const waiting: (() => void)[] = this.#stopped;

    this.#stopped = [];

    for (const wake of waiting)
      wake();
  }
}
